import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { User, Mail, Shield, Calendar, Building, Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export const SettingsPage = () => {
  const { user, api } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-50 text-red-700';
      case 'marketing_manager': return 'bg-blue-50 text-blue-700';
      case 'contributor': return 'bg-emerald-50 text-emerald-700';
      case 'viewer': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="mcc-card">
        <CardHeader>
          <CardTitle className="text-lg">Profile Information</CardTitle>
          <CardDescription>Your personal details and account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
              <p className="text-slate-500">{user?.email}</p>
              <Badge className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-slate-500 flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name
              </Label>
              <Input value={user?.name || ''} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input value={user?.email || ''} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Role
              </Label>
              <Input value={user?.role?.replace('_', ' ') || ''} disabled className="bg-slate-50 capitalize" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Member Since
              </Label>
              <Input 
                value={user?.created_at ? format(parseISO(user.created_at), 'MMMM d, yyyy') : ''} 
                disabled 
                className="bg-slate-50" 
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role Permissions */}
      <Card className="mcc-card">
        <CardHeader>
          <CardTitle className="text-lg">Role & Permissions</CardTitle>
          <CardDescription>Your access level in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {user?.role === 'admin' && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                <h4 className="font-semibold text-red-900 mb-2">Admin</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Full system control</li>
                  <li>• Manage all users and permissions</li>
                  <li>• Access to all projects, tasks, and content</li>
                  <li>• View and manage all dashboards</li>
                </ul>
              </div>
            )}
            {user?.role === 'marketing_manager' && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-900 mb-2">Marketing Manager</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Create and manage projects</li>
                  <li>• Assign tasks to team members</li>
                  <li>• Approve content and assets</li>
                  <li>• View all dashboards</li>
                </ul>
              </div>
            )}
            {user?.role === 'contributor' && (
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                <h4 className="font-semibold text-emerald-900 mb-2">Contributor</h4>
                <ul className="text-sm text-emerald-700 space-y-1">
                  <li>• Create and edit tasks</li>
                  <li>• Upload assets</li>
                  <li>• Submit content for approval</li>
                  <li>• View assigned projects</li>
                </ul>
              </div>
            )}
            {user?.role === 'viewer' && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Viewer</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  <li>• Read-only access</li>
                  <li>• View dashboards and reports</li>
                  <li>• Cannot create or edit content</li>
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="mcc-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Update Password</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card className="mcc-card">
        <CardHeader>
          <CardTitle className="text-lg">Integrations</CardTitle>
          <CardDescription>Connect external services (Coming in Phase 2)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-dashed border-slate-200 rounded-lg text-center">
              <div className="w-10 h-10 bg-slate-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                <Building className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">Slack</p>
              <p className="text-xs text-slate-400">Coming soon</p>
            </div>
            <div className="p-4 border border-dashed border-slate-200 rounded-lg text-center">
              <div className="w-10 h-10 bg-slate-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">Google Calendar</p>
              <p className="text-xs text-slate-400">Coming soon</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
